import { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import ActionBar from "../../../components/Shared/ActionBar";
import AdminLayout from "../../../components/Shared/AdminLayout";
import Container from "../../../components/Shared/Container";
import Pagination from "../../../components/Shared/Pagination";
import SubBrandList from "../../../components/Admin/SubBrands/SubBrandList";
import SubBrandModal from "../../../components/Admin/Brands/SubBrandModal";
import { buildSubBrandsCSV, downloadCSV } from "../../../components/Admin/SubBrands/SubBrandList/csv";

import * as adminHandlers from "../redux/handlers";

function SubBrands() {
    const dispatch = useDispatch();
    const { subBrands, subBrandsTotal, subBrandsLoading, error } = useSelector((state) => state.admin);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [selectedSubBrand, setSelectedSubBrand] = useState(null);
    const [selectedBrandId, setSelectedBrandId] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDebounce, setSearchDebounce] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        dispatch(adminHandlers.fetchAllSubBrandsHandler({
            page: currentPage,
            size: itemsPerPage,
            search: searchDebounce,
        }));
    }, [dispatch, currentPage, itemsPerPage, searchDebounce]);

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounce(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    }, []);

    const handleView = (row) => {
        if (!row?.subBrand) return;
        const brandId =
            row.subBrand.brand_id ||
            row.subBrand.brand?.id ||
            row.parentBrand?.id ||
            null;
        setSelectedSubBrand(row.subBrand);
        setSelectedBrandId(brandId);
        setTimeout(() => setIsViewModalOpen(true), 10);
    };

    const handlePageChange = (page) => setCurrentPage(page);

    const handleDownloadCSV = async () => {
        if (isExporting) return;
        try {
            setIsExporting(true);
            const PAGE_SIZE = 100;
            const buildParams = (page) => {
                const p = { page, size: PAGE_SIZE };
                if (searchDebounce) p.search = searchDebounce;
                return p;
            };
            const first = await adminHandlers.fetchSubBrandsPageRaw(buildParams(1));
            let all = first.items || [];
            const total = first.total || all.length;
            const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
            console.log(`[CSV] fetched page 1: ${all.length} items, total=${total}, pages=${totalPages}`);
            if (totalPages > 1) {
                const rest = await Promise.all(
                    Array.from({ length: totalPages - 1 }, (_, i) =>
                        adminHandlers.fetchSubBrandsPageRaw(buildParams(i + 2))
                    )
                );
                rest.forEach((r) => { all = all.concat(r.items || []); });
            }
            console.log(`[CSV] total rows: ${all.length}`);
            const csv = buildSubBrandsCSV(all);
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCSV(`sub-brands-${stamp}.csv`, csv);
        } catch (err) {
            console.error("Failed to download sub-brands CSV:", err);
            alert(`Failed to download CSV: ${err?.message || err}`);
        } finally {
            setIsExporting(false);
        }
    };

    const downloadButton = (
        <button
            type="button"
            onClick={handleDownloadCSV}
            disabled={isExporting}
            className="px-5 py-2.5 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 inline-flex items-center gap-2 text-sm leading-6 transition-colors disabled:opacity-60"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            <span>{isExporting ? "Exporting..." : "Download CSV"}</span>
        </button>
    );

    const rows = useMemo(
        () => (subBrands || []).map((sb) => {
            const brandList = Array.isArray(sb.brands) ? sb.brands : (sb.brand ? [sb.brand] : []);
            const parentBrand = brandList.length > 0
                ? {
                    id: brandList[0].id,
                    name: brandList.map((b) => b?.name).filter(Boolean).join(", "),
                    all: brandList,
                }
                : null;
            return { subBrand: sb, parentBrand };
        }),
        [subBrands]
    );

    const totalPages = Math.ceil(subBrandsTotal / itemsPerPage) || 1;

    return (
        <AdminLayout>
            <ActionBar
                title="Sub Brands Overview"
                showSearch={true}
                searchPlaceholder="Search"
                onSearch={handleSearch}
                additionalActions={downloadButton}
            />
            <Container>
                <div className="h-full flex flex-col justify-between">
                    {subBrandsLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500">Error: {error.data?.message || "Failed to load sub-brands"}</div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <SubBrandList rows={rows} onView={handleView} />
                            </div>
                            {rows.length > 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    totalItems={subBrandsTotal}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                />
                            )}
                            {searchQuery && rows.length === 0 && (
                                <div className="text-start">
                                    <p className="text-zinc-400">No sub-brands found matching "{searchQuery}"</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Container>

            {selectedSubBrand && (
                <SubBrandModal
                    subBrand={selectedSubBrand}
                    brandId={selectedBrandId}
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        setTimeout(() => {
                            setSelectedSubBrand(null);
                            setSelectedBrandId(null);
                        }, 300);
                    }}
                    onUpdate={(updatedSubBrand) => setSelectedSubBrand(updatedSubBrand)}
                />
            )}
        </AdminLayout>
    );
}

export default SubBrands;
